import React from "react";
import { Form, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import UploadFileDraggable from "../Form/UploadFileDraggable";
import { useTranslation } from "react-i18next";
import { FILE_TYPE, SIMULATION_TYPE } from "src/constant/common";
import { useForm } from "react-hook-form";
import { useRecoilState, useRecoilValue } from "recoil";
import { alertState, uploadingFile } from "src/recoil/atoms/global";
import { uploadMultipleState } from "src/recoil/atoms/uploadMultipleState";
import {
  convertFilesUploadDataToArray,
  duplicateFiles,
  findDuplicates,
  mapUploadedFilesToFormData,
  updateUploadMultipleState,
} from "src/utils/common";

import UploadMoreFiles from "../Form/UploadMoreFiles";
import { uploadedFilesState } from "src/recoil/atoms/uploadedFilesState";
import classNames from "classnames";
import { setRecoil } from "recoil-nexus";
import batchUpload from "src/Hooks/useBatchUpload";

export const FORM_NAME = {
  PARAMETER_JSON: "parameter_json",
  AERO_COEFFICIENTS: "aero_coefficients",
  AERO_COEFFICIENTS_1: "aero_coefficients_1",
  AERO_COEFFICIENTS_2: "aero_coefficients_2",
  WIND_MODEL: "wind_model",
  THRUST_PROFILE: "thrust_profile",
  KML_LINE: "km_line_files",
  REFERENCE_TRAJECTORY: "reference_trajectory_files",
};

const DEFAULT_MULTIPLE_STATE = {
  [FORM_NAME.PARAMETER_JSON]: true,
  [FORM_NAME.AERO_COEFFICIENTS]: true,
  [FORM_NAME.AERO_COEFFICIENTS_1]: true,
  [FORM_NAME.AERO_COEFFICIENTS_2]: true,
  [FORM_NAME.WIND_MODEL]: true,
  [FORM_NAME.THRUST_PROFILE]: true,
};

const DEFAULT_FORM_VALUES = {
  [FORM_NAME.PARAMETER_JSON]: [],
  [FORM_NAME.AERO_COEFFICIENTS]: [],
  [FORM_NAME.AERO_COEFFICIENTS_1]: [],
  [FORM_NAME.AERO_COEFFICIENTS_2]: [],
  [FORM_NAME.WIND_MODEL]: [],
  [FORM_NAME.THRUST_PROFILE]: [],
  [FORM_NAME.KML_LINE]: [],
  [FORM_NAME.REFERENCE_TRAJECTORY]: [],
};

const ModalUploadFile3DOF = ({ paramsInput, toggle, setToggle, onModalSubmit, isNewVersion, isDebrisImpact = false }) => {
  const { t } = useTranslation("flight-simulator");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    clearErrors,
    reset,
    getValues,
  } = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
  });
  const [globalUploadingField, setGlobalUploadingField] = useRecoilState(uploadingFile);
  const [uploadMultiple, setUploadMultiple] = useRecoilState(uploadMultipleState);
  const uploadedFiles = useRecoilValue(uploadedFilesState);
  const watchKmlLineFiles = getValues(FORM_NAME.KML_LINE);
  const watchReferenceTrajectoryFiles = getValues(FORM_NAME.REFERENCE_TRAJECTORY);
  const watchAeroCoefficientsFiles = getValues(FORM_NAME.AERO_COEFFICIENTS);

  const handleFileUpload = async (fieldName, files) => {
    if (!files) {
      return;
    }

    setValue(fieldName, files);
    clearErrors(fieldName);
    if (fieldName === FORM_NAME.KML_LINE || fieldName === FORM_NAME.AERO_COEFFICIENTS || fieldName === FORM_NAME.REFERENCE_TRAJECTORY) {
      return;
    }

    const updateState = updateUploadMultipleState(getValues(), uploadMultiple);
    setUploadMultiple(updateState);
  };

  const handleModalFileDelete = (fieldName, files, fileSelected) => {
    setValue(fieldName, files);
    clearErrors(fieldName);
    const updateState = updateUploadMultipleState(getValues(), uploadMultiple);
    setUploadMultiple(updateState);
  };

  const onSubmit = async (data) => {
    setToggle(false);

    try {
      const allFiles = convertFilesUploadDataToArray(data, SIMULATION_TYPE["3DOF"], [FORM_NAME.KML_LINE, FORM_NAME.AERO_COEFFICIENTS, FORM_NAME.REFERENCE_TRAJECTORY]);
      const hasRowDuplicated = findDuplicates(paramsInput, allFiles);
      if (hasRowDuplicated?.length > 0) {
        setRecoil(alertState, () => ({
          status: true,
          message: t("new.validate_duplicate_file_name"),
          type: "danger",
        }));
        setUploadMultiple(DEFAULT_MULTIPLE_STATE);
        reset(DEFAULT_FORM_VALUES);
        return;
      }

      if (!uploadedFiles || uploadedFiles.length === 0) {
        onModalSubmit(allFiles);
        return;
      }

      setGlobalUploadingField(true);

      const fileContents = uploadedFiles.map((item) => item?.file);

      const duplicatedFiles = duplicateFiles(data, fileContents, [FORM_NAME.AERO_COEFFICIENTS]);
      const results = await batchUpload(duplicatedFiles);
      const updatedData = mapUploadedFilesToFormData(allFiles, results, [FORM_NAME.AERO_COEFFICIENTS]);
      onModalSubmit(updatedData);
      reset(DEFAULT_FORM_VALUES);
      setGlobalUploadingField(false);
      setUploadMultiple(DEFAULT_MULTIPLE_STATE);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancel = () => {
    setUploadMultiple(DEFAULT_MULTIPLE_STATE);
    reset(DEFAULT_FORM_VALUES);
    setToggle(false);
  };

  const isShowUploadMoreFilesButton = (fieldName) => {
    const isMultiple = uploadMultiple[fieldName];
    const hasItems = getValues(fieldName)?.length > 0;

    return isMultiple && hasItems;
  };

  return (
    <Modal centered isOpen={toggle} toggle={handleCancel} size="lg" backdrop="static">
      <ModalHeader>{t("new.input_files")}</ModalHeader>
      <ModalBody className="p-y-0 max-vh-78 overflow-auto">
        <Form className="p-x-15" onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-md-12">
              <div>
                <UploadFileDraggable
                  classGroup={classNames({
                    "mb-0": isShowUploadMoreFilesButton(FORM_NAME.PARAMETER_JSON),
                  })}
                  name={FORM_NAME.PARAMETER_JSON}
                  setValue={(files) => handleFileUpload(FORM_NAME.PARAMETER_JSON, files)}
                  label={`${t("new.parameter_json")} json`}
                  requiredMessage={t("new.required_attachments")}
                  clearErrors={clearErrors}
                  errors={errors}
                  register={register}
                  fileTypeAllow={[FILE_TYPE.JSON]}
                  isMultiple={uploadMultiple.parameter_json}
                  required
                  onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.PARAMETER_JSON, files, fileSelected)}
                  initialFiles={getValues(FORM_NAME.PARAMETER_JSON)}
                  checkDuplicatedFileNames
                  ignoreUploadS3
                  simulationType={SIMULATION_TYPE["3DOF"]}
                  isSubmitting={isSubmitting}
                />
                {isShowUploadMoreFilesButton(FORM_NAME.PARAMETER_JSON) && (
                  <UploadMoreFiles
                    className="mb-3"
                    onFileSelect={handleFileUpload}
                    fieldName={FORM_NAME.PARAMETER_JSON}
                    isDisabled={!!globalUploadingField}
                    fileTypeAllow={[FILE_TYPE.JSON]}
                    checkDuplicatedFileNames
                    initialFiles={getValues(FORM_NAME.PARAMETER_JSON)}
                    simulationType={SIMULATION_TYPE["3DOF"]}
                  />
                )}
              </div>

              {isNewVersion ? (
                <div>
                  <UploadFileDraggable
                    classGroup={classNames({
                      "mb-0": watchAeroCoefficientsFiles?.length > 0,
                    })}
                    name={FORM_NAME.AERO_COEFFICIENTS}
                    setValue={(files) => handleFileUpload(FORM_NAME.AERO_COEFFICIENTS, files)}
                    label={`${t("new.aero_coefficients")} csv or xlsx (option)`}
                    clearErrors={clearErrors}
                    errors={errors}
                    register={register}
                    fileTypeAllow={[FILE_TYPE.CSV, FILE_TYPE.XLSX]}
                    isMultiple
                    onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.AERO_COEFFICIENTS, files, fileSelected)}
                    initialFiles={getValues(FORM_NAME.AERO_COEFFICIENTS)}
                    checkDuplicatedFileNames
                    ignoreUploadS3
                    simulationType={SIMULATION_TYPE["3DOF"]}
                    isSubmitting={isSubmitting}
                    sameFileTypeRequired={isNewVersion}
                  />
                  {watchAeroCoefficientsFiles?.length > 0 && (
                    <UploadMoreFiles
                      className="mb-3"
                      onFileSelect={handleFileUpload}
                      fieldName={FORM_NAME.AERO_COEFFICIENTS}
                      isDisabled={!!globalUploadingField}
                      fileTypeAllow={[FILE_TYPE.CSV, FILE_TYPE.XLSX]}
                      checkDuplicatedFileNames
                      initialFiles={getValues(FORM_NAME.AERO_COEFFICIENTS)}
                      simulationType={SIMULATION_TYPE["3DOF"]}
                      sameFileTypeRequired={isNewVersion}
                    />
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <UploadFileDraggable
                      classGroup={classNames({
                        "mb-0": isShowUploadMoreFilesButton(FORM_NAME.AERO_COEFFICIENTS_1),
                      })}
                      name={FORM_NAME.AERO_COEFFICIENTS_1}
                      setValue={(files) => handleFileUpload(FORM_NAME.AERO_COEFFICIENTS_1, files)}
                      label={`${t("new.aero_coefficients_1")} csv or xlsx (option)`}
                      clearErrors={clearErrors}
                      errors={errors}
                      register={register}
                      fileTypeAllow={[FILE_TYPE.CSV, FILE_TYPE.XLSX]}
                      isMultiple={uploadMultiple.aero_coefficients_1}
                      onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.AERO_COEFFICIENTS_1, files, fileSelected)}
                      initialFiles={getValues(FORM_NAME.AERO_COEFFICIENTS_1)}
                      checkDuplicatedFileNames
                      ignoreUploadS3
                      simulationType={SIMULATION_TYPE["3DOF"]}
                      isSubmitting={isSubmitting}
                    />
                    {isShowUploadMoreFilesButton(FORM_NAME.AERO_COEFFICIENTS_1) && (
                      <UploadMoreFiles
                        className="mb-3"
                        onFileSelect={handleFileUpload}
                        fieldName={FORM_NAME.AERO_COEFFICIENTS_1}
                        isDisabled={!!globalUploadingField}
                        fileTypeAllow={[FILE_TYPE.CSV, FILE_TYPE.XLSX]}
                        checkDuplicatedFileNames
                        initialFiles={getValues(FORM_NAME.AERO_COEFFICIENTS_1)}
                        simulationType={SIMULATION_TYPE["3DOF"]}
                      />
                    )}
                  </div>

                  <div>
                    <UploadFileDraggable
                      classGroup={classNames({
                        "mb-0": isShowUploadMoreFilesButton(FORM_NAME.AERO_COEFFICIENTS_2),
                      })}
                      name={FORM_NAME.AERO_COEFFICIENTS_2}
                      setValue={(files) => handleFileUpload(FORM_NAME.AERO_COEFFICIENTS_2, files)}
                      label={`${t("new.aero_coefficients_2")} csv or xlsx (option)`}
                      clearErrors={clearErrors}
                      errors={errors}
                      register={register}
                      fileTypeAllow={[FILE_TYPE.CSV, FILE_TYPE.XLSX]}
                      isMultiple={uploadMultiple.aero_coefficients_2}
                      onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.AERO_COEFFICIENTS_2, files, fileSelected)}
                      initialFiles={getValues(FORM_NAME.AERO_COEFFICIENTS_2)}
                      checkDuplicatedFileNames
                      ignoreUploadS3
                      simulationType={SIMULATION_TYPE["3DOF"]}
                      isSubmitting={isSubmitting}
                    />
                    {isShowUploadMoreFilesButton(FORM_NAME.AERO_COEFFICIENTS_2) && (
                      <UploadMoreFiles
                        className="mb-3"
                        onFileSelect={handleFileUpload}
                        fieldName={FORM_NAME.AERO_COEFFICIENTS_2}
                        isDisabled={!!globalUploadingField}
                        fileTypeAllow={[FILE_TYPE.CSV, FILE_TYPE.XLSX]}
                        checkDuplicatedFileNames
                        initialFiles={getValues(FORM_NAME.AERO_COEFFICIENTS_2)}
                        simulationType={SIMULATION_TYPE["3DOF"]}
                      />
                    )}
                  </div>
                </>
              )}

              <div>
                <UploadFileDraggable
                  classGroup={classNames({
                    "mb-0": isShowUploadMoreFilesButton(FORM_NAME.WIND_MODEL),
                  })}
                  name={FORM_NAME.WIND_MODEL}
                  setValue={(files) => handleFileUpload(FORM_NAME.WIND_MODEL, files)}
                  label={`${t("new.wind_model")} csv (option)`}
                  clearErrors={clearErrors}
                  errors={errors}
                  register={register}
                  fileTypeAllow={[FILE_TYPE.CSV]}
                  isMultiple={uploadMultiple.wind_model}
                  onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.WIND_MODEL, files, fileSelected)}
                  initialFiles={getValues(FORM_NAME.WIND_MODEL)}
                  checkDuplicatedFileNames
                  ignoreUploadS3
                  simulationType={SIMULATION_TYPE["3DOF"]}
                  isSubmitting={isSubmitting}
                />
                {isShowUploadMoreFilesButton(FORM_NAME.WIND_MODEL) && (
                  <UploadMoreFiles
                    className="mb-3"
                    onFileSelect={handleFileUpload}
                    fieldName={FORM_NAME.WIND_MODEL}
                    isDisabled={!!globalUploadingField}
                    fileTypeAllow={[FILE_TYPE.CSV]}
                    checkDuplicatedFileNames
                    initialFiles={getValues(FORM_NAME.WIND_MODEL)}
                    simulationType={SIMULATION_TYPE["3DOF"]}
                  />
                )}
              </div>

              <div>
                <UploadFileDraggable
                  classGroup={classNames({
                    "mb-0": isShowUploadMoreFilesButton(FORM_NAME.THRUST_PROFILE),
                  })}
                  name={FORM_NAME.THRUST_PROFILE}
                  setValue={(files) => handleFileUpload(FORM_NAME.THRUST_PROFILE, files)}
                  label={`${t("new.thrust_profile")} csv (option)`}
                  clearErrors={clearErrors}
                  errors={errors}
                  register={register}
                  fileTypeAllow={[FILE_TYPE.CSV]}
                  isMultiple={uploadMultiple.thrust_profile}
                  onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.THRUST_PROFILE, files, fileSelected)}
                  initialFiles={getValues(FORM_NAME.THRUST_PROFILE)}
                  checkDuplicatedFileNames
                  ignoreUploadS3
                  simulationType={SIMULATION_TYPE["3DOF"]}
                  isSubmitting={isSubmitting}
                />
                {isShowUploadMoreFilesButton(FORM_NAME.THRUST_PROFILE) && (
                  <UploadMoreFiles
                    className="mb-3"
                    onFileSelect={handleFileUpload}
                    fieldName={FORM_NAME.THRUST_PROFILE}
                    isDisabled={!!globalUploadingField}
                    fileTypeAllow={[FILE_TYPE.CSV]}
                    checkDuplicatedFileNames
                    initialFiles={getValues(FORM_NAME.THRUST_PROFILE)}
                    simulationType={SIMULATION_TYPE["3DOF"]}
                  />
                )}
              </div>

              {isDebrisImpact && (
                <div>
                  <UploadFileDraggable
                    classGroup={classNames({
                      "mb-0": watchReferenceTrajectoryFiles?.length > 0,
                    })}
                    name={FORM_NAME.REFERENCE_TRAJECTORY}
                    setValue={(files) => handleFileUpload(FORM_NAME.REFERENCE_TRAJECTORY, files)}
                    label={`${t("new.reference_trajectory_label")}`}
                    clearErrors={clearErrors}
                    errors={errors}
                    register={register}
                    fileTypeAllow={[FILE_TYPE.CSV]}
                    isMultiple
                    onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.REFERENCE_TRAJECTORY, files, fileSelected)}
                    initialFiles={getValues(FORM_NAME.REFERENCE_TRAJECTORY)}
                    checkDuplicatedFileNames
                    ignoreUploadS3
                    simulationType={SIMULATION_TYPE["3DOF"]}
                    isSubmitting={isSubmitting}
                  />
                  {watchReferenceTrajectoryFiles?.length > 0 && (
                    <UploadMoreFiles
                      className="mb-3"
                      onFileSelect={handleFileUpload}
                      fieldName={FORM_NAME.REFERENCE_TRAJECTORY}
                      isDisabled={!!globalUploadingField}
                      fileTypeAllow={[FILE_TYPE.CSV]}
                      checkDuplicatedFileNames
                      initialFiles={getValues(FORM_NAME.REFERENCE_TRAJECTORY)}
                      simulationType={SIMULATION_TYPE["3DOF"]}
                    />
                  )}
                </div>
              )}

              <div>
                <UploadFileDraggable
                  classGroup={classNames({
                    "mb-0": watchKmlLineFiles?.length > 0,
                  })}
                  name={FORM_NAME.KML_LINE}
                  setValue={(files) => handleFileUpload(FORM_NAME.KML_LINE, files)}
                  label={`${t("new.kml_line_label")}`}
                  clearErrors={clearErrors}
                  errors={errors}
                  register={register}
                  fileTypeAllow={[FILE_TYPE.KML]}
                  isMultiple
                  onDeleteFile={(files, fileSelected) => handleModalFileDelete(FORM_NAME.KML_LINE, files, fileSelected)}
                  initialFiles={getValues(FORM_NAME.KML_LINE)}
                  checkDuplicatedFileNames
                  ignoreUploadS3
                  simulationType={SIMULATION_TYPE["3DOF"]}
                  isSubmitting={isSubmitting}
                />
                {watchKmlLineFiles?.length > 0 && (
                  <UploadMoreFiles
                    className="mb-3"
                    onFileSelect={handleFileUpload}
                    fieldName={FORM_NAME.KML_LINE}
                    isDisabled={!!globalUploadingField}
                    fileTypeAllow={[FILE_TYPE.KML]}
                    checkDuplicatedFileNames
                    initialFiles={getValues(FORM_NAME.KML_LINE)}
                    simulationType={SIMULATION_TYPE["3DOF"]}
                  />
                )}
              </div>
            </div>
          </div>
        </Form>
      </ModalBody>
      <ModalFooter className="justify-content-center p-t-10 p-b-20">
        <button className="btn btn-sm btn-secondary m-x-10 width-by-px-120" onClick={handleCancel}>
          {t("new.cancel")}
        </button>
        <button
          className="btn btn-sm btn-primary m-x-10 width-by-px-120"
          disabled={!!globalUploadingField || isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {t("new.register")}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default ModalUploadFile3DOF;
